import { AUTH_TOKEN_KEY } from '@/lib/authRefresh';

export const IMPERSONATOR_TOKEN_KEY = 'impersonator_token';
export const IMPERSONATION_META_KEY = 'impersonation_meta';

export interface ImpersonationTargetUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles?: string[];
}

export interface ImpersonationMeta {
  targetUser: ImpersonationTargetUser;
  writesEnabled: boolean;
  expiresAt: string;
}

export function getImpersonationMeta(): ImpersonationMeta | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationMeta;
    if (!parsed?.targetUser?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function isImpersonating(): boolean {
  return getImpersonationMeta() !== null && !!localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
}

export function startImpersonationSession(result: {
  impersonationToken: string;
  expiresAt: string | Date;
  writesEnabled?: boolean;
  targetUser: ImpersonationTargetUser;
}): void {
  const current = localStorage.getItem(AUTH_TOKEN_KEY);
  if (current) {
    localStorage.setItem(IMPERSONATOR_TOKEN_KEY, current);
  }

  localStorage.setItem(AUTH_TOKEN_KEY, result.impersonationToken);
  const meta: ImpersonationMeta = {
    targetUser: result.targetUser,
    writesEnabled: result.writesEnabled === true,
    expiresAt:
      typeof result.expiresAt === 'string'
        ? result.expiresAt
        : result.expiresAt.toISOString(),
  };
  localStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify(meta));

  // Clear legacy keys if present
  localStorage.removeItem('impersonation_token');
  localStorage.removeItem('impersonation_expires');
  localStorage.removeItem('impersonation_user');
}

export function updateImpersonationToken(result: {
  impersonationToken: string;
  expiresAt: string | Date;
  writesEnabled?: boolean;
  targetUser?: ImpersonationTargetUser;
}): void {
  localStorage.setItem(AUTH_TOKEN_KEY, result.impersonationToken);
  const existing = getImpersonationMeta();
  if (!existing && !result.targetUser) return;

  const meta: ImpersonationMeta = {
    targetUser: result.targetUser ?? existing!.targetUser,
    writesEnabled: result.writesEnabled === true,
    expiresAt:
      typeof result.expiresAt === 'string'
        ? result.expiresAt
        : result.expiresAt.toISOString(),
  };
  localStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify(meta));
}

export function stopImpersonationSession(): { restored: boolean } {
  const backup = localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATION_META_KEY);
  localStorage.removeItem('impersonation_token');
  localStorage.removeItem('impersonation_expires');
  localStorage.removeItem('impersonation_user');

  if (backup) {
    localStorage.setItem(AUTH_TOKEN_KEY, backup);
    return { restored: true };
  }

  localStorage.removeItem(AUTH_TOKEN_KEY);
  return { restored: false };
}
